import {
  PRICING_CALCULATOR_PLATFORM_FEE,
  PricingCalculatorConfig,
  PricingCalculatorRange,
  PricingCalculatorResult,
  PricingCalculatorResultLine,
} from '@app-model/pricing-calculator.model';
import { ValueConfig } from '@app/views/private/admin/model/advertisers';

export function roundHalfUp(value: number): number {
  if (value < 0) {
    return -Math.round(-value);
  }
  return Math.round(value);
}

export function roundPriceToStep(value: number, step: number | null | undefined): number {
  if (step == null || step <= 0) {
    return roundHalfUp(value);
  }

  return roundHalfUp(value / step) * step;
}

export function ceilPriceToStep(value: number, step: number | null | undefined): number {
  if (step == null || step <= 0) {
    return Math.ceil(value);
  }

  // Descarta resíduo de ponto flutuante para não subir um degrau inteiro em múltiplos exatos.
  const steps = Number((value / step).toFixed(6));
  return Math.ceil(steps) * step;
}

export function getExpectedPriceRoundingLabel(step: number | null | undefined): string {
  if (step == null) {
    return 'Nenhum';
  }

  return `R$ ${step.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
}

export function formatPricingCurrency(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) {
    return 'R$ 0';
  }
  return `R$ ${roundHalfUp(value).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
}

export function lookupDealerMargin(
  ranges: PricingCalculatorRange[],
  marketValue: number,
): { margin: number; index: number; sorted: PricingCalculatorRange[] } {
  const sorted = [...ranges].sort((a, b) => a.minMarketValue - b.minMarketValue);
  let margin: number | null = null;
  let index = -1;

  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i].minMarketValue <= marketValue) {
      margin = sorted[i].dealerMargin;
      index = i;
    }
  }

  return {
    margin: margin ?? 0,
    index,
    sorted,
  };
}

export function buildRangeLabel(sorted: PricingCalculatorRange[], index: number): string {
  if (index < 0 || !sorted.length) {
    return '—';
  }

  const from = sorted[index].minMarketValue;
  if (index === sorted.length - 1) {
    return `A partir de ${formatPricingCurrency(from)}`;
  }

  return `De ${formatPricingCurrency(from)} até ${formatPricingCurrency(sorted[index + 1].minMarketValue - 1)}`;
}

export function rangeMinToFormValue(minMarketValue: number, index: number): number {
  return index === 0 ? minMarketValue : minMarketValue - 1;
}

export function formValueToRangeMin(formValue: number, index: number): number {
  return index === 0 ? formValue : formValue + 1;
}

export function buildPercentLabel(part: number, total: number): string {
  if (total <= 0) {
    return '';
  }
  const pct = Math.round((part / total) * 1000) / 10;
  return formatPercentageLabel(pct);
}

export function formatPercentageLabel(value: number): string {
  const pct = Math.round(value * 10) / 10;
  return `(${pct.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%)`;
}

export interface PricingBreakdownValues {
  marketValue: number;
  dealerMargin: number;
  dealerMarginPercentage?: number | null;
  dealerCosts: number;
  platformFee: number;
  paymentValue: number;
  platformCommission: number;
  commissionPercent: number;
}

export function buildPricingBreakdownLines(
  values: PricingBreakdownValues,
): PricingCalculatorResultLine[] {
  const marginPercentLabel = values.dealerMarginPercentage != null
    ? formatPercentageLabel(values.dealerMarginPercentage)
    : buildPercentLabel(values.dealerMargin, values.marketValue);
  const commissionLabel = String(values.commissionPercent).replace('.', ',');

  const lines: PricingCalculatorResultLine[] = [
    { label: 'Valor de mercado', value: formatPricingCurrency(values.marketValue) },
    {
      label: `− Margem do lojista ${marginPercentLabel}`.trim(),
      value: `−${formatPricingCurrency(values.dealerMargin)}`,
      muted: true,
      negative: true,
    },
    {
      label: '− Custos do lojista',
      value: `−${formatPricingCurrency(values.dealerCosts)}`,
      muted: true,
      negative: true,
    },
  ];

  if (values.platformFee > 0) {
    lines.push({
      label: '− Custo do lojista (Fee plataforma)',
      value: `−${formatPricingCurrency(values.platformFee)}`,
      muted: true,
      negative: true,
    });
  }

  lines.push(
    {
      label: '= Valor da paga',
      value: formatPricingCurrency(values.paymentValue),
      strong: true,
      highlightRow: true,
    },
    {
      label: `− Comissão plataforma (${commissionLabel}%)`,
      value: `−${formatPricingCurrency(values.platformCommission)}`,
      muted: true,
      negative: true,
    },
  );

  return lines;
}

export function resolveSegmentComissionFee(
  config: ValueConfig | null | undefined,
  defaultFee: number = PRICING_CALCULATOR_PLATFORM_FEE,
): number {
  if (!config || config.type === 'DEFAULT') {
    return defaultFee;
  }

  if (config.type === 'REAL_AND_PERCENTAGE') {
    return config.additionalFee === 0 ? defaultFee : config.additionalFee;
  }

  if (config.type === 'REAL') {
    return config.value ?? defaultFee;
  }

  return defaultFee;
}

export function resolveOemDealerPlatformFee(
  segmentComissionConfig: Map<string, ValueConfig> | Record<string, ValueConfig> | null | undefined,
  segment = 'CAR',
): number {
  if (!segmentComissionConfig) {
    return resolveSegmentComissionFee(undefined);
  }

  const config = segmentComissionConfig instanceof Map
    ? segmentComissionConfig.get(segment)
    : segmentComissionConfig[segment];

  return resolveSegmentComissionFee(config);
}

export function calculateSuggestedPrice(
  config: PricingCalculatorConfig,
  marketValue: number,
  dealerCosts: number,
  platformFeeAmount: number = PRICING_CALCULATOR_PLATFORM_FEE,
): PricingCalculatorResult | null {
  if (!config?.ranges?.length || marketValue <= 0) {
    return null;
  }

  const lookup = lookupDealerMargin(config.ranges, marketValue);
  const dealerMargin = lookup.margin;
  const platformFee = config.includePlatformFeeInCosts ? platformFeeAmount : 0;
  const paymentValue = roundHalfUp(marketValue - dealerMargin - dealerCosts - platformFee);
  const platformCommission = roundHalfUp(paymentValue * (config.platformCommissionPercent / 100));
  const suggestedPrice = roundPriceToStep(
    roundHalfUp(paymentValue - platformCommission),
    config.expectedPriceRounding,
  );

  return {
    marketValue,
    dealerCosts,
    dealerMargin,
    platformFee,
    paymentValue,
    platformCommission,
    suggestedPrice,
    appliedRangeIndex: lookup.index,
    appliedRangeLabel: buildRangeLabel(lookup.sorted, lookup.index),
    marketValuePercent: marketValue > 0 ? (suggestedPrice / marketValue) * 100 : 0,
    breakdownLines: buildPricingBreakdownLines({
      marketValue,
      dealerMargin,
      dealerCosts,
      platformFee,
      paymentValue,
      platformCommission,
      commissionPercent: config.platformCommissionPercent,
    }),
  };
}

export function createDefaultPricingCalculatorConfig(): PricingCalculatorConfig {
  return {
    ranges: [{ minMarketValue: 0, dealerMargin: 0 }],
    platformCommissionPercent: 0,
    includePlatformFeeInCosts: false,
    requiredPrice: false,
    listPriceMarginPercentage: null,
  };
}

export function suggestListPriceFromMargin(
  price: number,
  listPriceMarginPercentage: number,
  roundingStep: number | null | undefined = null,
): number {
  const raw = price * (1 + listPriceMarginPercentage / 100);
  const normalizedToCents = Math.round(raw * 100) / 100;
  return ceilPriceToStep(normalizedToCents, roundingStep);
}

export function normalizePricingCalculatorRanges(
  ranges: PricingCalculatorRange[],
): PricingCalculatorRange[] {
  const normalized = ranges
    .map((range) => ({
      minMarketValue: roundHalfUp(range.minMarketValue),
      dealerMargin: roundHalfUp(range.dealerMargin),
    }))
    .filter((range) => range.minMarketValue >= 0 && range.dealerMargin >= 0)
    .sort((a, b) => a.minMarketValue - b.minMarketValue);

  if (!normalized.some((range) => range.minMarketValue === 0)) {
    normalized.unshift({ minMarketValue: 0, dealerMargin: normalized[0]?.dealerMargin ?? 0 });
  }

  const uniqueMinValues = new Set<number>();
  return normalized.filter((range) => {
    if (uniqueMinValues.has(range.minMarketValue)) {
      return false;
    }
    uniqueMinValues.add(range.minMarketValue);
    return true;
  });
}

export function resolveInitialMarketValue(
  webPrice: number | null | undefined,
  fipePrice: number | null | undefined,
): number | null {
  if (webPrice != null && webPrice > 0) {
    return webPrice;
  }
  if (fipePrice != null && fipePrice > 0) {
    return fipePrice;
  }
  return null;
}
